import React, {
  useState,
  useEffect,
  ChangeEvent,
  useRef,
  useMemo,
} from "react";
import "./AutoCompleteClient.css";
import { searchClientByName } from "../../services/clientService";
import StyledLabel from "../Label/StyledLabel";

export interface Client {
  id: number;
  corporateName: string;
}

interface AutoCompleteClientProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (client: Client) => void;
  label?: string;
}

function stripAccents(s: string) {
  return (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

export function AutoCompleteClient({
  value,
  onChange,
  onSelect,
  label,
}: AutoCompleteClientProps) {
  const [options, setOptions] = useState<Client[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filterTerm, setFilterTerm] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const data = await searchClientByName("");
        if (mounted) {
          const uniq = Array.from(new Map((data || []).map((c: { id: any; }) => [c.id, c])).values());
          setOptions(uniq as any);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const term = stripAccents(filterTerm.trim());
    if (!term) return options;
    return options.filter(c => stripAccents(c.corporateName).includes(term));
  }, [options, filterTerm]);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    onChange(v);       
    setFilterTerm(v); 
    setOpen(true);     
  }

  function handleOpen() {
    setOpen(true);
    setFilterTerm("");
  }

  function handleSelect(cli: Client) {
    onChange(cli.corporateName);
    onSelect(cli);
    setFilterTerm(""); 
    setOpen(false);
  }

  return (
    <div className="form-group auto-complete-container" ref={containerRef}>
      <StyledLabel htmlFor="client-autocomplete">{label}</StyledLabel>

      <input
        id="client-autocomplete"
        className="form-control auto-complete-input"
        type="text"
        value={value}
        onChange={handleChange}
        onClick={handleOpen}      
        aria-autocomplete="list"
        aria-controls="client-autocomplete-list"
        aria-expanded={open}
        autoComplete="off"
      />

      {open && (
        <ul
          id="client-autocomplete-list"
          className="auto-complete-suggestions"
          role="listbox"
        >
          {loading && (
            <li className="auto-complete-loading" role="option">
              Carregando...
            </li>
          )}

          {!loading && filtered.length === 0 && (
            <li className="auto-complete-no-results" role="option">
              Nenhum cliente encontrado
            </li>
          )}

          {!loading &&
            filtered.map(cli => (
              <li
                key={cli.id}
                className="auto-complete-suggestion"
                role="option"
                onClick={() => handleSelect(cli)}
                title={cli.corporateName}
              >
                <span>{cli.corporateName}</span>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
